'use client';

import * as React from 'react';

import { Tabs as TabsPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';

/**
 * Tab appearance. Set it once on `TabsList`; the triggers read it from context.
 *
 * - `underline` — design-system §6: an underline under the active tab and a
 *   hover surface, nothing else. No track, no filled pill, no shadow. This is
 *   what the house rules ask for and what the Pricing & Packages tabs render.
 * - `segmented` — the inherited shadcn look (a `bg-muted` track with a raised
 *   white pill). Still the default ONLY because dozens of screens were built on
 *   it; migrating them is its own pass. Do not choose it for new UI.
 */
type TabsVariant = 'segmented' | 'underline';

const TabsVariantContext = React.createContext<TabsVariant>('segmented');

const Tabs = TabsPrimitive.Root;

const TabsList: React.FC<
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: TabsVariant;
  }
> = ({ className, variant = 'segmented', ...props }) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      className={cn(
        'text-muted-foreground',
        variant === 'underline'
          ? // The list is just a hairline the triggers sit on; `-mb-px` on the
            // triggers pulls their 2px indicator over it so the active one
            // reads as a continuation of the rule, not a bar beneath it.
            'border-border flex h-auto items-center justify-start gap-6 border-b bg-transparent p-0'
          : 'bg-muted inline-flex h-10 items-center justify-center rounded-md p-1',
        className,
      )}
      {...props}
    />
  </TabsVariantContext.Provider>
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger: React.FC<
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
> = ({ className, ...props }) => {
  const variant = React.useContext(TabsVariantContext);

  return (
    <TabsPrimitive.Trigger
      className={cn(
        'ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center text-sm font-medium whitespace-nowrap focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50',
        variant === 'underline'
          ? 'text-muted-foreground hover:border-border hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-foreground -mb-px rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 shadow-none transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none'
          : // `transition-all`, not `transition-colors`: the segmented pill
            // animates its box-shadow too, and every existing screen on this
            // variant expects that. Keep it byte-identical to what shipped.
            'data-[state=active]:bg-background data-[state=active]:text-foreground rounded-xs px-3 py-1.5 transition-all data-[state=active]:shadow-xs',
        className,
      )}
      {...props}
    />
  );
};
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent: React.FC<
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
> = ({ className, ...props }) => (
  <TabsPrimitive.Content
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden',
      className,
    )}
    {...props}
  />
);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
export type { TabsVariant };
